import { sanitizeMjcfName } from "../../utils/mjcfNames";
import type { QuaternionLikeXyzw } from "./xmlGenerationTypes";

export const arrayToString = (values: Array<number | string>): string =>
  values.map((value) => formatMjcfNumber(value)).join(" ");

export const formatMjcfNumber = (value: number | string): string => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Math.abs(numeric) < 1e-12) return "0";
  const rounded = Number(numeric.toPrecision(10));
  return Object.is(rounded, -0) ? "0" : String(rounded);
};

export const xmlEscapeAttr = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const colorToRgba = (color: unknown): [number, number, number, number] | null => {
  if (typeof color !== "string") return null;
  const value = color.trim();
  if (!value) return null;
  const hex = value.startsWith("#") ? value.slice(1) : value;
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) return null;
  const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const b = Number.parseInt(hex.slice(4, 6), 16) / 255;
  const a = hex.length >= 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1;
  return [r, g, b, a];
};

export const toNumberArray = (values: unknown, length: number): number[] | null => {
  if (!Array.isArray(values) || values.length !== length) {
    return null;
  }
  const numeric = values.map((value) => Number(value));
  return numeric.some((value) => !Number.isFinite(value)) ? null : numeric;
};

export const normalizeQuat = (values: unknown): number[] => {
  const numeric = toNumberArray(values, 4);
  if (!numeric) {
    return [1, 0, 0, 0];
  }
  const [w, x, y, z] = numeric;
  const magnitude = Math.hypot(w, x, y, z);
  if (!Number.isFinite(magnitude) || magnitude < 1e-8) {
    return [1, 0, 0, 0];
  }
  return [w / magnitude, x / magnitude, y / magnitude, z / magnitude];
};

export const toMjcfQuatWxyz = (values: unknown): number[] => normalizeQuat(values);

export const threeQuatXyzwToWxyz = (q: QuaternionLikeXyzw): number[] =>
  normalizeQuat([Number(q?.w), Number(q?.x), Number(q?.y), Number(q?.z)]);

export const quatMultiply = (a: number[], b: number[]): number[] => {
  const [aw, ax, ay, az] = normalizeQuat(a);
  const [bw, bx, by, bz] = normalizeQuat(b);
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
};

export const quatConjugate = (qRaw: number[]): number[] => {
  const [w, x, y, z] = normalizeQuat(qRaw);
  return [w, -x, -y, -z];
};

export const quatRotateVec = (qRaw: number[], vRaw: number[]): number[] => {
  const q = normalizeQuat(qRaw);
  const v = [0, Number(vRaw[0]) || 0, Number(vRaw[1]) || 0, Number(vRaw[2]) || 0];
  const qi = [q[0], -q[1], -q[2], -q[3]];
  const qv = quatMultiply(quatMultiply(q, v), qi);
  return [qv[1], qv[2], qv[3]];
};

// Viewer world uses a Y-up shim relative to MuJoCo world:
// mujoco -> viewer: [x, y, z] -> [x, z, -y]  (Rx(-90deg))
// viewer -> mujoco: [x, y, z] -> [x, -z, y]  (Rx(+90deg))
export const viewerVecToMujoco = (vRaw: number[]): number[] => {
  const x = Number(vRaw?.[0]) || 0;
  const y = Number(vRaw?.[1]) || 0;
  const z = Number(vRaw?.[2]) || 0;
  return [x, -z, y];
};

export const qViewerToMujoco = [Math.SQRT1_2, Math.SQRT1_2, 0, 0];

export const viewerQuatToMujoco = (qRaw: number[]): number[] =>
  quatMultiply(qViewerToMujoco, normalizeQuat(qRaw));

export const reserveUniqueName = (
  preferredRaw: string | undefined | null,
  fallbackRaw: string,
  usedNames: Set<string>
): string => {
  const preferred = sanitizeMjcfName(preferredRaw || "") || sanitizeMjcfName(fallbackRaw);
  let candidate = preferred;
  let suffix = 1;
  while (usedNames.has(candidate)) {
    candidate = sanitizeMjcfName(`${preferred}-${suffix}`);
    suffix += 1;
  }
  usedNames.add(candidate);
  return candidate;
};
